import React from 'react';
import {
    Chip,
    Paper,
    Switch as MuiSwitch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useTranslation } from 'react-i18next';
import { IconBtnBase } from 'Components';

/**
 * Every certificate name list: its name, how many names it holds, and whether
 * it is active — with the switch that changes that, and the list's actions.
 *
 * Clicking a row selects the list whose names the table below shows. The
 * action buttons stop the click so they do not also change the selection.
 */
const CertificateListsPanel = ({ lists, selectedId, busyId, onSelect, onToggleActive, onImport, onEdit, onDelete }) => {
    const { t } = useTranslation();
    const stop = (handler) => (event) => {
        event.stopPropagation();
        handler();
    };

    return (
        <TableContainer component={Paper}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>{t('admin.certificateNames.lists.name')}</TableCell>
                        <TableCell align="right">{t('admin.certificateNames.lists.names')}</TableCell>
                        <TableCell>{t('admin.certificateNames.lists.status')}</TableCell>
                        <TableCell>{t('admin.certificateNames.lists.activeSwitch')}</TableCell>
                        <TableCell align="right">{t('common.actions')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {lists.map((list) => (
                        <TableRow
                            key={list.id}
                            hover
                            selected={list.id === selectedId}
                            onClick={() => onSelect(list.id)}
                            sx={{ cursor: 'pointer' }}
                        >
                            <TableCell>
                                <div className="flex items-center gap-2">
                                    <Typography variant="body2" fontWeight={list.id === selectedId ? 600 : 400}>
                                        {list.name}
                                    </Typography>
                                    {list.is_default && (
                                        <Chip size="small" variant="outlined" label={t('admin.certificateNames.lists.default')} />
                                    )}
                                </div>
                                {list.description && (
                                    <Typography variant="caption" color="text.secondary">
                                        {list.description}
                                    </Typography>
                                )}
                            </TableCell>
                            <TableCell align="right">{Number(list.names_count).toLocaleString()}</TableCell>
                            <TableCell>
                                <Chip
                                    size="small"
                                    color={list.is_active ? 'success' : 'default'}
                                    label={
                                        list.is_active
                                            ? t('admin.certificateNames.lists.active')
                                            : t('admin.certificateNames.lists.inactive')
                                    }
                                />
                            </TableCell>
                            <TableCell onClick={(event) => event.stopPropagation()}>
                                <MuiSwitch
                                    checked={Boolean(list.is_active)}
                                    disabled={busyId === list.id}
                                    onChange={(event) => onToggleActive(list, event.target.checked)}
                                    inputProps={{
                                        'aria-label': t('admin.certificateNames.lists.activeSwitch'),
                                    }}
                                />
                            </TableCell>
                            <TableCell align="right">
                                <div className="flex items-center justify-end">
                                    <IconBtnBase
                                        title={t('admin.certificateNames.lists.importInto')}
                                        onClick={stop(() => onImport(list))}
                                        size="large"
                                    >
                                        <UploadFileIcon />
                                    </IconBtnBase>
                                    <IconBtnBase title={t('common.edit')} onClick={stop(() => onEdit(list))} size="large">
                                        <BorderColorOutlinedIcon />
                                    </IconBtnBase>
                                    <Tooltip
                                        title={
                                            list.is_default
                                                ? t('admin.certificateNames.lists.defaultUndeletable')
                                                : t('common.delete')
                                        }
                                    >
                                        {/* A disabled button fires no events, so the tooltip needs a wrapper. */}
                                        <span>
                                            <IconBtnBase
                                                onClick={stop(() => onDelete(list))}
                                                size="large"
                                                disabled={list.is_default}
                                            >
                                                <DeleteOutlineOutlinedIcon />
                                            </IconBtnBase>
                                        </span>
                                    </Tooltip>
                                </div>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default CertificateListsPanel;
